import { useEffect, useId, useRef, useState } from "react";
import { Code } from "@connectrpc/connect";
import { useQuery } from "@connectrpc/connect-query";
import { createFileRoute } from "@tanstack/react-router";

import { formatNumber } from "@yatayat/core/money";
import { StaffRole } from "@yatayat/gen/yatayat/auth/v1/auth_pb";
import { RegistryService, type Sticker } from "@yatayat/gen/yatayat/registry/v1/registry_pb";

import { useStaffSessionExpiry } from "~/components/OperatorTrip";
import { Loading, QueryError } from "~/components/QueryState";
import { StickerArt, StickerPrint, bodyTypeLabels } from "~/components/Sticker";
import { useI18n } from "~/lib/i18n";
import { vehicleClassLabels } from "~/lib/labels";
import { printPage } from "~/lib/native";
import { errorCode, retryTransient } from "~/lib/rpc/errors";
import { can, useStaffSession } from "~/lib/staff";
import { ui } from "~/lib/ui";

export const Route = createFileRoute("/operator/stickers")({
  component: OperatorStickers,
});

// Keeps a print job short enough to check every page before it goes to the printer.
const maxPerPrint = 20;

function OperatorStickers() {
  const { t, lang } = useI18n();
  const allowed = can.printStickers(useStaffSession().session?.role ?? StaffRole.UNSPECIFIED);
  const fleet = useQuery(RegistryService.method.listStickers, {}, { enabled: allowed, retry: retryTransient });
  const unauthenticated = useStaffSessionExpiry(fleet.error);
  const denied = fleet.isError && errorCode(fleet.error) === Code.PermissionDenied;
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());
  const hintId = useId();

  if (!allowed || denied) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <h1 className={ui.h1}>{t("stickersTitle")}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("stickersNotAllowed")}</p>
      </div>
    );
  }

  const stickers = fleet.data?.stickers ?? [];
  // In fleet order; a vehicle that left the fleet since it was ticked drops out.
  const selected = stickers.filter((s) => picked.has(s.vehicleId));
  const overLimit = selected.length > maxPerPrint;
  const preview = selected[0] ?? stickers[0];

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const toggleAll = () => setPicked(selected.length === stickers.length ? new Set() : new Set(stickers.map((s) => s.vehicleId)));

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="space-y-2">
        <h1 className={ui.h1}>{t("stickersTitle")}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("stickersIntro")}</p>
      </div>

      {fleet.isPending && <Loading />}
      {fleet.isError && !fleet.data && !unauthenticated && <QueryError onRetry={() => void fleet.refetch()} busy={fleet.isFetching} />}
      {fleet.data && stickers.length === 0 && (
        <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-600 dark:border-slate-700 dark:text-slate-400">
          {t("stickersEmpty")}
        </p>
      )}

      {stickers.length > 0 && (
        <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_15rem] md:items-start">
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <button
                  type="button"
                  disabled={selected.length === 0 || overLimit}
                  onClick={printPage}
                  aria-describedby={hintId}
                  className="rounded-md bg-ink px-4 py-2 text-sm font-medium text-white disabled:opacity-60 dark:bg-ink-dark dark:text-slate-950"
                >
                  {t("stickersPrint")}
                </button>
                <p className="text-sm font-medium tabular-nums">{t("stickersSelected", { n: formatNumber(selected.length, lang) })}</p>
              </div>
              <div id={hintId} aria-live="polite" className="text-sm">
                {overLimit ? (
                  <p className="font-medium text-amber-800 dark:text-amber-300">
                    {t("stickersOverLimit", { n: formatNumber(selected.length, lang), max: formatNumber(maxPerPrint, lang) })}
                  </p>
                ) : selected.length === 0 ? (
                  <p className="text-slate-600 dark:text-slate-400">{t("stickersNoneSelected")}</p>
                ) : null}
                <p className="text-slate-600 dark:text-slate-400">{t("stickersLimit", { max: formatNumber(maxPerPrint, lang) })}</p>
              </div>
            </div>

            <FleetList stickers={stickers} picked={picked} selectedCount={selected.length} onToggle={toggle} onToggleAll={toggleAll} />
          </div>

          {preview && (
            <figure className="mx-auto w-full max-w-[15rem] space-y-2 md:sticky md:top-4">
              <div className="overflow-hidden rounded-lg border border-slate-300 shadow-sm dark:border-slate-700">
                <StickerArt sticker={preview} className="aspect-[105/148] w-full" />
              </div>
              <figcaption className="text-center text-sm text-slate-600 dark:text-slate-400">
                {t("stickersPreview", { plate: preview.plateNo })}
              </figcaption>
            </figure>
          )}
        </div>
      )}

      {selected.length > 0 && <StickerPrint stickers={selected.slice(0, maxPerPrint)} />}
    </div>
  );
}

function FleetList({
  stickers,
  picked,
  selectedCount,
  onToggle,
  onToggleAll,
}: {
  stickers: Sticker[];
  picked: ReadonlySet<string>;
  selectedCount: number;
  onToggle: (vehicleId: string) => void;
  onToggleAll: () => void;
}) {
  const { t, lang } = useI18n();
  const headingId = useId();
  const all = selectedCount === stickers.length;
  const selectAll = useRef<HTMLInputElement>(null);

  // Only settable from script: shows a part-selected fleet as neither ticked nor clear.
  useEffect(() => {
    if (selectAll.current) selectAll.current.indeterminate = selectedCount > 0 && !all;
  }, [selectedCount, all]);

  const checkbox = "h-4 w-4 flex-none accent-ink dark:accent-ink-dark";

  return (
    <section aria-labelledby={headingId} className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
      <h2 id={headingId} className="sr-only">
        {t("stickersFleet")}
      </h2>
      <label className="flex cursor-pointer items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium dark:border-slate-800 dark:bg-slate-900">
        <input ref={selectAll} type="checkbox" checked={all} onChange={onToggleAll} className={checkbox} />
        {t("stickersSelectAll", { n: formatNumber(stickers.length, lang) })}
      </label>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {stickers.map((sticker) => {
          const body = bodyTypeLabels[sticker.bodyType];
          const details = [t(vehicleClassLabels[sticker.vehicleClass]), sticker.electric && t("electric"), body && t(body)].filter(Boolean).join(" · ");

          return (
            <li key={sticker.vehicleId}>
              <label className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-slate-50/60 dark:hover:bg-slate-900/60">
                <input
                  type="checkbox"
                  checked={picked.has(sticker.vehicleId)}
                  onChange={() => onToggle(sticker.vehicleId)}
                  className={`${checkbox} mt-1`}
                />
                <span className="min-w-0 flex-1">
                  {/* Kept as registered, since it must match the plate on the bus. */}
                  <span className="block font-mono font-semibold text-slate-800 dark:text-slate-200">{sticker.plateNo}</span>
                  <span className="block text-sm text-slate-600 dark:text-slate-400">{details}</span>
                  {sticker.routeCode ? (
                    <span className="block text-sm">{sticker.routeName?.[lang] || sticker.routeCode}</span>
                  ) : (
                    <span className="block text-sm font-medium text-amber-800 dark:text-amber-300">{t("stickersNoPermit")}</span>
                  )}
                </span>
                <span className="shrink-0 font-mono text-xs text-slate-500 dark:text-slate-400">{sticker.stickerCode}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
